import { RaoSourceContext } from '../../services/nuclia-service';

/**
 * Builds a mapping of citation blocks to their associated links and source.
 * 
 * @param citationMetadata - Metadata from answer_citations containing block -> {contextId, originUrls} mappings
 * @param sourceContexts - Array of source contexts from ARAG responses with chunks containing URLs
 * @returns A record mapping block IDs to {urls, source} objects
 */
export function buildCitationLinks(
    citationMetadata: Record<string, { contextId: string; originUrls: string[] }> | undefined,
    sourceContexts: RaoSourceContext[]
): Record<string, { urls: string[]; source: string; title: string; summary?: string }> {
    if (!citationMetadata) return {};

    const result: Record<string, { urls: string[]; source: string; title: string; summary?: string }> = {};

    // Create a map of contextId -> context for quick lookup
    const contextMap = new Map<string, RaoSourceContext>();
    for (const context of sourceContexts) {
        if (context.contextId) {
            contextMap.set(context.contextId, context);
        }
    }

    // For each block in citation metadata, gather URLs
    for (const [blockId, blockData] of Object.entries(citationMetadata)) {
        const { contextId, originUrls } = blockData;
        const context = contextMap.get(contextId);

        if (!context) {
            console.warn(`[buildCitationLinks] No context found for contextId: ${contextId}`);
            continue;
        }

        // Gather all URLs from the context's chunks
        const chunkUrls: string[] = [];
        for (const chunk of context.chunks) {
            if (chunk.url && chunk.url.length > 0) {
                chunkUrls.push(...chunk.url);
            }
        }

        // Combine origin_urls from block metadata with chunk URLs
        const allUrls = [...originUrls, ...chunkUrls];

        // Deduplicate URLs
        const uniqueUrls = Array.from(new Set(allUrls)).filter(url => url && url.trim() !== '');

        result[blockId] = {
            urls: uniqueUrls,
            source: context.source || 'Unknown Source',
            title: context.title || 'Untitled',
            summary: context.summary
        };
    }

    return result;
}
